import { useCallback, useMemo } from "react";

import {
  UPDATE_ORGANIZATION_PAGE_TAB_IDS,
  UPDATE_ORGANIZATION_PAGE_TAB_LABELS,
  UPDATE_PLATFORM_PAGE_TAB_IDS,
  UPDATE_PLATFORM_PAGE_TAB_LABELS,
} from "@/constants";
import { useCurrentLeague, useCurrentOrganization, useCurrentUser } from "@/contexts";
import { useCanViewPlatformSettings, useOrganizationPermissions } from "@/hooks";
import { useT } from "@/i18n";

import { getLeagueNavItemPath } from "../DashboardSidebar/LeagueNav/useLeagueNavGroups";
import { useVisibleLeagueNavGroups } from "../DashboardSidebar/LeagueNav/useVisibleLeagueNavGroups";
import { useGetNavLinkPath, useGetNavLinksGroupLabel, useGetNavLinkLabel } from "../NavLink";
import { useNavLinkGroups } from "../NavLink/useNavLinkGroups";

import { PageSearchItem } from "./GlobalSearch.types";

export const useGetPageSearchItems = () => {
  const t = useT();
  const getNavLinkLabel = useGetNavLinkLabel();
  const getNavLinkPath = useGetNavLinkPath();
  const getNavLinksGroupLabel = useGetNavLinksGroupLabel();
  const navLinkGroups = useNavLinkGroups();

  const currentOrganization = useCurrentOrganization();
  const currentLeague = useCurrentLeague();
  const visibleLeagueNavGroups = useVisibleLeagueNavGroups(currentLeague);
  // The update-platform page is reachable with any platform capability (it
  // renders read-only without `PLATFORM_SETTINGS:WRITE`).
  const canViewPlatformSettings = useCanViewPlatformSettings();
  const isSuperAdmin = useCurrentUser().role === "superAdmin";
  const { canViewOrganization, canUpdateOrganization } = useOrganizationPermissions();
  // The update-organization page is reachable with `ORG:READ` alone (it renders
  // read-only without `ORG:WRITE`), so search surfaces its tabs on either key.
  const canOpenCurrentOrganization = canViewOrganization || canUpdateOrganization;

  const PAGES: PageSearchItem[] = useMemo(() => {
    const navPages: PageSearchItem[] = navLinkGroups.flatMap((group) =>
      group.items.map((item) => ({
        id: item.type,
        type: "page",
        title: getNavLinkLabel(item.type),
        to: getNavLinkPath(item.type),
        path: [getNavLinksGroupLabel(group.type)],
      }))
    );

    const leaguePages: PageSearchItem[] = currentLeague
      ? visibleLeagueNavGroups.flatMap((group) =>
          group.items.map((item) => ({
            id: `league-page:${currentLeague.id}:${item.type}`,
            type: "page",
            title: t(item.labelKey),
            to: `/league/${currentLeague.id}/${getLeagueNavItemPath(item)}`,
            path: [
              currentLeague.title ?? t("general.globalSearch.itemTypes.league.league"),
              t(group.labelKey),
            ],
          }))
        )
      : [];

    // Settings tabs of the current platform / organization, gated by the same
    // view permission the pages enforce. `scopedHref` (applied in
    // useGetSearchItemTarget) handles the platform/org URL scope; emit the bare
    // path here. Skipped entirely when the user can't even view, so they're
    // never surfaced as a navigation target.
    const platformTabPages: PageSearchItem[] = canViewPlatformSettings
      ? UPDATE_PLATFORM_PAGE_TAB_IDS.filter(
          // `extras` is superadmin-only on the page itself, so surfacing it here
          // for anyone else discloses the tab name and dead-ends on General.
          (tabId) => tabId !== "extras" || isSuperAdmin
        ).map((tabId) => ({
          id: `platform-settings-tab:${tabId}`,
          type: "page",
          title: t(UPDATE_PLATFORM_PAGE_TAB_LABELS[tabId]),
          // Target the `current` sentinel so search lands on the active platform's
          // settings without baking in its id (mirrors the organization tabs).
          to: `/platforms/manage/current/${tabId}`,
          path: [t("platform.pages.updatePlatform.title")],
        }))
      : [];

    const organizationTabPages: PageSearchItem[] =
      canOpenCurrentOrganization && currentOrganization
        ? UPDATE_ORGANIZATION_PAGE_TAB_IDS.map((tabId) => ({
            id: `organization-settings-tab:${tabId}`,
            type: "page",
            title: t(UPDATE_ORGANIZATION_PAGE_TAB_LABELS[tabId]),
            to: `/entities/edit/current/${tabId}`,
            path: [t("organization.pages.updateOrganization.title")],
          }))
        : [];

    // A selected league is an exclusive search context. Mixing platform,
    // federation, or global dashboard destinations back into these results
    // makes the scope ambiguous and bypasses the league sidebar's RBAC model.
    return currentLeague
      ? leaguePages
      : [...navPages, ...platformTabPages, ...organizationTabPages];
  }, [
    t,
    isSuperAdmin,
    getNavLinkLabel,
    getNavLinkPath,
    getNavLinksGroupLabel,
    navLinkGroups,
    currentLeague,
    visibleLeagueNavGroups,
    currentOrganization,
    canViewPlatformSettings,
    canOpenCurrentOrganization,
  ]);

  const getPageSearchItems = useCallback(
    (search: string) => {
      const normalizedSearch = search.toLowerCase();
      return PAGES.filter(
        (page) =>
          page.title.toLowerCase().includes(normalizedSearch) ||
          // Stable nav ids double as locale-independent aliases. This makes
          // searches such as "integrations" resolve even when the visible
          // translated label uses different wording.
          page.id.toLowerCase().includes(normalizedSearch)
      );
    },
    [PAGES]
  );

  return getPageSearchItems;
};
